import React, { useState, useEffect, useRef } from 'react';
import { baseUrl } from '../config';

if (typeof document !== 'undefined' && !document.getElementById('promote-project-styles')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'promote-project-styles';
  styleEl.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(styleEl);
}

const StatusBadge = ({ status }) => {
  const map = {
    active:          { label: '● Live',           bg: '#052e16', color: '#4ade80', border: '#166534' },
    pending_payment: { label: '◷ Pending Payment', bg: '#1c1917', color: '#fbbf24', border: '#92400e' },
    expired:         { label: '○ Expired',         bg: '#1f2937', color: '#9ca3af', border: '#374151' },
    cancelled:       { label: '✕ Cancelled',       bg: '#1f2937', color: '#6b7280', border: '#374151' },
    payment_failed:  { label: '✕ Payment Failed',  bg: '#1c0a0a', color: '#f87171', border: '#7f1d1d' },
  };
  const cfg = map[status] || { label: status, bg: '#1f2937', color: '#9ca3af', border: '#374151' };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
      {cfg.label}
    </span>
  );
};

const DAY_OPTIONS = [
  { days: 1,  label: '1 Day' },
  { days: 3,  label: '3 Days' },
  { days: 7,  label: '1 Week', popular: true },
  { days: 14, label: '2 Weeks' },
  { days: 30, label: '1 Month' },
];

const PRICE_PER_DAY = 199;

const ImageUploadZone = ({ imagePreview, onImageChange, uploading }) => {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) onImageChange(file);
  };

  return (
    <div
      onClick={() => !uploading && inputRef.current.click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      style={{
        border: `2px dashed ${dragging ? '#008080' : imagePreview ? '#065f46' : '#1f2937'}`,
        borderRadius: 12,
        cursor: uploading ? 'wait' : 'pointer',
        background: dragging ? '#0f2027' : '#0a0f1e',
        transition: 'all 0.2s',
        overflow: 'hidden',
        height: imagePreview ? 220 : 150,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      {imagePreview ? (
        <>
          <img src={imagePreview} alt="Poster preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <div
            style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: '0.2s' }}
            onMouseEnter={e => e.currentTarget.style.opacity = 1}
            onMouseLeave={e => e.currentTarget.style.opacity = 0}
          >
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 13, background: 'rgba(0,0,0,0.6)', padding: '8px 16px', borderRadius: 8 }}>
              🖼️ Click to change image
            </span>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: 24 }}>
          {uploading ? (
            <>
              <div style={{ width: 36, height: 36, border: '3px solid #1f2937', borderTop: '3px solid #008080', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
              <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>Uploading your image...</p>
            </>
          ) : (
            <>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🖼️</div>
              <p style={{ color: '#e5e7eb', fontSize: 14, fontWeight: 600, margin: '0 0 6px' }}>Upload Your Project Poster or Banner</p>
              <p style={{ color: '#4b5563', fontSize: 12, margin: 0 }}>Drag & drop here, or click to browse · JPG, PNG, GIF · Max 10MB</p>
            </>
          )}
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) onImageChange(e.target.files[0]); }} />
    </div>
  );
};

const StepLabel = ({ step, label, optional }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
    <span style={{ background: '#008080', color: '#fff', width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
      {step}
    </span>
    <span style={{ fontWeight: 600, fontSize: 14, color: '#d1d5db' }}>{label}</span>
    {optional && <span style={{ color: '#4b5563', fontSize: 11 }}>(optional)</span>}
  </div>
);

const inputStyle = {
  width: '100%', background: '#0a0f1e',
  border: '1px solid #1f2937', borderRadius: 8,
  padding: '11px 14px', color: '#e5e7eb',
  fontSize: 14, boxSizing: 'border-box', outline: 'none',
};

const PromoteProject = () => {
  const [view, setView]                 = useState('list');
  const [myAds, setMyAds]               = useState([]);
  const [projects, setProjects]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [submitting, setSubmitting]     = useState(false);
  const [uploading, setUploading]       = useState(false);
  const [error, setError]               = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [form, setForm] = useState({ project_id: '', title: '', description: '', img: '', days: 7 });

  const token   = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchMyAds = async (signal) => {
    try {
      setLoading(true);
      const res = await fetch(`${baseUrl}/feature-project/my-ads`, { headers, signal });
      if (res.ok) setMyAds(await res.json());
    } catch (err) {
      if (err.name === 'AbortError') return; // tab switched — ignore
      setError('Could not load your promotions.');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async (signal) => {
    try {
      const userRes = await fetch(`${baseUrl}/getUserIDByToken`, { headers, signal });
      if (!userRes.ok) return;
      const { user_id } = await userRes.json();
      const res = await fetch(`${baseUrl}/getProjectsByUserId`, { headers: { ...headers, user_id }, signal });
      if (res.ok) setProjects((await res.json()).projects || []);
    } catch (err) {
      if (err.name === 'AbortError') return; // tab switched — ignore
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchMyAds(controller.signal);
    fetchProjects(controller.signal);
    return () => controller.abort(); // ✅ cancel on unmount/tab switch
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProjectSelect = (e) => {
    const pid  = parseInt(e.target.value);
    const proj = projects.find(p => p.project_id === pid);
    setForm(f => ({
      ...f,
      project_id:  pid || '',
      title:       proj ? proj.title        : f.title,
      description: proj ? proj.project_desc : f.description,
    }));
  };

  const handleImageChange = async (file) => {
    if (file.size > 10485760) { setError('Image too large. Maximum 10MB allowed.'); return; }

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);

    try {
      setUploading(true);
      setError('');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('file_type', 'image');
      fd.append('user_id', 0);

      const res = await fetch(`${baseUrl}/upload/direct`, { method: 'POST', body: fd });
      if (res.ok) {
        const data = await res.json();
        setForm(f => ({ ...f, img: data.public_url }));
      } else {
        setError('Image upload failed. Please try again.');
        setImagePreview('');
      }
    } catch (err) {
      setError('Image upload failed. Please try again.');
      setImagePreview('');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    if (!form.title.trim()) { setError('Please enter a title for your promotion.'); return; }
    if (uploading)           { setError('Please wait for the image to finish uploading.'); return; }

    try {
      setSubmitting(true);
      const res = await fetch(`${baseUrl}/feature-project/initiate`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id:     form.project_id || null,
          title:          form.title,
          description:    form.description,
          img:            form.img || null,
          url:            null,
          days:           form.days,
          amount_per_day: PRICE_PER_DAY,
        }),
      });
      if (!res.ok) {
        const e = await res.json();
        setError(e.detail || 'Something went wrong.');
        return;
      }
      const data = await res.json();
      window.location.href = data.payment_url;
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (ad_id) => {
    if (!window.confirm('Cancel this promotion?')) return;
    try {
      const res = await fetch(`${baseUrl}/feature-project/${ad_id}/cancel`, { method: 'DELETE', headers });
      if (res.ok) {
        const controller = new AbortController();
        fetchMyAds(controller.signal);
      } else {
        const e = await res.json();
        alert(e.detail || 'Cannot cancel.');
      }
    } catch (err) {
      alert('Network error.');
    }
  };

  const resetForm = () => {
    setForm({ project_id: '', title: '', description: '', img: '', days: 7 });
    setImagePreview('');
    setError('');
  };

  // ── Pre-fill form from existing ad (Renew / Edit) ──────────────────────────
  const prefillForm = (ad) => {
    setForm({
      project_id:  '',
      title:       ad.title       || '',
      description: ad.description || '',
      img:         ad.img         || '',
      days:        ad.days        || 7,
    });
    setImagePreview(ad.img || '');
    setError('');
    setView('create');
  };

  const totalCost = form.days * PRICE_PER_DAY;

  // ── LIST VIEW ────────────────────────────────────────────────────────────────
  if (view === 'list') {
    return (
      <div style={{ color: '#e5e7eb' }} suppressHydrationWarning={true}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>🎬 Feature Your Project</h2>
          <button
            onClick={() => { resetForm(); setView('create'); }}
            style={{ background: '#facc15', color: '#000', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
          >
            + New Promotion
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}>
            <div style={{ width: 36, height: 36, border: '3px solid #1f2937', borderTop: '3px solid #008080', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 14px' }} />
            Loading your promotions...
          </div>
        ) : myAds.length === 0 ? (
          <div style={{ background: '#0a0f1e', borderRadius: 16, padding: '64px 40px', textAlign: 'center', border: '1px dashed #1f2937' }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>📢</div>
            <h3 style={{ fontWeight: 700, marginBottom: 8, fontSize: 18 }}>No promotions yet</h3>
            <p style={{ color: '#6b7280', fontSize: 14, maxWidth: 380, margin: '0 auto 28px' }}>
              Feature your project on the homepage to attract collaborators, film enthusiasts, and marketing talent.
            </p>
            <button
              onClick={() => { resetForm(); setView('create'); }}
              style={{ background: '#facc15', color: '#000', border: 'none', borderRadius: 8, padding: '12px 28px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
            >
              Create First Promotion
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {myAds.map(ad => (
              <div key={ad.ad_id} style={{ background: '#0a0f1e', borderRadius: 12, padding: '14px 18px', display: 'flex', gap: 14, alignItems: 'center', border: ad.status === 'active' ? '1px solid #065f46' : '1px solid #1f2937' }}>
                <div style={{ width: 68, height: 68, borderRadius: 8, overflow: 'hidden', background: '#111827', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>
                  {ad.img ? <img src={ad.img} alt={ad.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '🎬'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>{ad.title}</span>
                    <StatusBadge status={ad.status} />
                  </div>
                  <div style={{ color: '#6b7280', fontSize: 12, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    <span>📅 {ad.days} day{ad.days > 1 ? 's' : ''}</span>
                    {ad.start_date && <span>Started {new Date(ad.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>}
                    {ad.end_date   && <span>Ends {new Date(ad.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                  </div>
                </div>
                {ad.status === 'active' && (
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                      onClick={() => window.open(`/display-project/${ad.ad_id}`, '_blank')}
                      style={{ background: '#0f2027', border: '1px solid #065f46', color: '#4ade80', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
                    >
                      👁️ View
                    </button>
                    <button
                      onClick={() => prefillForm(ad)}
                      style={{ background: '#1e3a5f', border: '1px solid #2563eb', color: '#93c5fd', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
                    >
                      ✏️ Edit
                    </button>
                  </div>
                )}
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                      onClick={() => prefillForm(ad)}
                      style={{ background: '#1e3a5f', border: '1px solid #2563eb', color: '#93c5fd', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12 }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleCancel(ad.ad_id)}
                      style={{ background: 'transparent', border: '1px solid #374151', color: '#9ca3af', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12 }}
                    >
                      Cancel
                    </button>
                  </div>
                )
                {ad.status === 'expired' && (
                  <button
                    onClick={() => prefillForm(ad)}
                    style={{ background: '#1a1200', border: '1px solid #facc15', color: '#facc15', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700, flexShrink: 0 }}
                  >
                    🔄 Renew
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── CREATE VIEW ──────────────────────────────────────────────────────────────
  return (
    <div style={{ color: '#e5e7eb', maxWidth: 580 }} suppressHydrationWarning={true}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 32 }}>
        <button
          onClick={() => setView('list')}
          style={{ background: '#111827', border: '1px solid #1f2937', color: '#e5e7eb', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
        >
          ← Back
        </button>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
            {form.title ? '✏️ Edit Promotion' : 'Promote Your Project'}
          </h2>
          <p style={{ color: '#6b7280', fontSize: 12, margin: '2px 0 0' }}>
            {form.title
              ? 'Update your details and go live again'
              : 'Complete the steps below — your project goes live immediately after payment'}
          </p>
        </div>
      </div>

      {error && (
        <div style={{ background: '#1c0a0a', border: '1px solid #7f1d1d', borderRadius: 8, padding: '10px 16px', color: '#fca5a5', fontSize: 13, marginBottom: 20 }}>
          ⚠️ {error}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

        <div>
          <StepLabel step="1" label="Add a Poster or Banner Image" optional />
          <ImageUploadZone imagePreview={imagePreview} onImageChange={handleImageChange} uploading={uploading} />
          <p style={{ color: '#374151', fontSize: 11, margin: '6px 0 0' }}>
            This will be the first thing people see on the homepage. A good poster grabs attention.
          </p>
        </div>

        {projects.length > 0 && (
          <div>
            <StepLabel step="2" label="Link to One of Your Existing Projects" optional />
            <select value={form.project_id} onChange={handleProjectSelect} style={inputStyle}>
              <option value="">— Pick a project to auto-fill details —</option>
              {projects.map(p => (
                <option key={p.project_id} value={p.project_id}>{p.title}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <StepLabel step={projects.length > 0 ? "3" : "2"} label="Promotion Title" />
          <input
            type="text"
            placeholder='e.g. "The Last Horizon — Now Casting Open Roles"'
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            style={inputStyle}
            data-gramm="false"
            data-gramm_editor="false"
            data-enable-grammarly="false"
            spellCheck="false"
          />
        </div>

        <div>
          <StepLabel step={projects.length > 0 ? "4" : "3"} label="What Should People Know?" optional />
          <textarea
            placeholder='e.g. "A thriller set in the mountains of Himachal Pradesh. Looking for passionate crew members and marketing collaborators to join our team."'
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            rows={4}
            style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.7 }}
            data-gramm="false"
            data-gramm_editor="false"
            data-enable-grammarly="false"
            spellCheck="false"
          />
        </div>

        <div>
          <StepLabel step={projects.length > 0 ? "5" : "4"} label="How Long Should It Be Featured?" />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {DAY_OPTIONS.map(opt => (
              <button
                key={opt.days}
                onClick={() => setForm(f => ({ ...f, days: opt.days }))}
                style={{
                  position: 'relative',
                  padding: '10px 20px', borderRadius: 10,
                  border: form.days === opt.days ? '2px solid #facc15' : '2px solid #1f2937',
                  background: form.days === opt.days ? '#1a1200' : '#0a0f1e',
                  color: form.days === opt.days ? '#facc15' : '#9ca3af',
                  cursor: 'pointer', fontWeight: 700, fontSize: 13,
                  transition: 'all 0.15s',
                }}
              >
                {opt.label}
                {opt.popular && (
                  <span style={{ position: 'absolute', top: -9, right: -2, background: '#facc15', color: '#000', fontSize: 8, fontWeight: 900, padding: '1px 6px', borderRadius: 10, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                    Popular
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div style={{ background: '#0a0f1e', border: '1px solid #1f2937', borderRadius: 12, padding: '18px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Featured for</div>
            <div style={{ fontWeight: 800, fontSize: 17 }}>{DAY_OPTIONS.find(o => o.days === form.days)?.label}</div>
            <div style={{ color: '#374151', fontSize: 11, marginTop: 3 }}>Goes live instantly · No auto-renewal</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Total</div>
            <div style={{ fontWeight: 900, fontSize: 28, color: '#facc15', lineHeight: 1 }}>₹{totalCost.toLocaleString('en-IN')}</div>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={submitting || uploading}
          style={{
            background: (submitting || uploading) ? '#1f2937' : '#008080',
            color: (submitting || uploading) ? '#6b7280' : '#fff',
            border: 'none', borderRadius: 12, padding: '16px',
            fontWeight: 800, fontSize: 15,
            cursor: (submitting || uploading) ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s', letterSpacing: 0.2,
          }}
        >
          {uploading  ? '⏳ Uploading image...' :
           submitting ? '⏳ Redirecting to payment...' :
           `🚀 Go Live · Pay ₹${totalCost.toLocaleString('en-IN')}`}
        </button>

        <p style={{ color: '#374151', fontSize: 11, textAlign: 'center', margin: 0 }}>
          You'll be taken to a secure payment page. Your project appears on the homepage the moment payment is confirmed.
        </p>

      </div>
    </div>
  );
};

export default PromoteProject;
